import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { DraftJob } from '@/features/jobs/drafting/DraftJob'
import type {
  StepAccessor,
  StepFactory,
} from '@/features/jobs/drafting/OutlineViewModel'
import type { QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import type { QuestionStore } from '@/features/jobs/questions/QuestionStore'
import type { AnswersMap } from '@/features/jobs/questions/questions'
import type { QuestionEditorViewModel } from '@/features/jobs/questions/QuestionEditorViewModel'
import { action, computed, observable, runInAction } from 'mobx'
import { ViewModelList } from '@/infra/view-model-list'
import { type Observable, Subject, switchMap } from 'rxjs'
import { withAbortSignal } from '@/utils/rx-util'
import { isAbortError } from '@/utils/abort-util'
import type {
  DraftingError,
  DraftStateSlice,
} from '@/features/jobs/drafting/DraftJobViewModel'
import type { DraftChange } from '@/features/jobs/drafting/DraftPersister'
import { map } from 'rxjs/operators'
import { task } from 'mobx-task'
import { toStream } from '@/utils/mobx-util'

/**
 * View-model that manages the onboarding questionnaire.
 */
export class QuestionnaireViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _fetchErrorSubject = new Subject<DraftingError>()

  /**
   * View-Model list of questions with each question's associated step.
   */
  private readonly _questionVms

  /**
   * Emits whenever fetching questions errors. The emitted value
   * is the error.
   */
  readonly fetchError$ = this._fetchErrorSubject.asObservable()

  /**
   * Emits changes to persist.
   */
  readonly changes$: Observable<DraftChange>

  /**
   * Emits when the question VMs want to move the user to the next question.
   */
  readonly continue$: Observable<void>

  /**
   * Stalls the Continue action while we're fetching questions.
   */
  readonly isStallingContinue$ = toStream(
    () => this._fetchQuestions.pending,
    true,
  )

  /**
   * Whether the questionnaire is completed.
   */
  @observable accessor isQuestionnaireCompleted = false

  constructor(
    private readonly _job: DraftJob,
    createStep: StepFactory,
    private readonly _isReadOnly: () => boolean,
    private readonly _questionListVm: QuestionListViewModel,
    private readonly _questionStore: QuestionStore,
  ) {
    this.changes$ = this._questionListVm.answersChanged$.pipe(
      map((answers) => ({ type: 'UpdateAnswers', answers })),
    )

    this.continue$ = this._questionListVm.continue$

    this._lifecycle.manage(this._questionListVm)
    this._questionVms = new ViewModelList({
      models: () => this._questionListVm.questionVms.slice(),
      create: (questionVm) =>
        new QuestionStepViewModel(questionVm, this._isReadOnly, createStep),
    })
    this._lifecycle.manage(this._questionVms)

    // Query for questions when answers are changed.
    this._lifecycle.manage(
      this._questionListVm.answersChanged$.pipe(
        switchMap((answers) =>
          withAbortSignal((signal) => this._fetchQuestions(answers, signal)),
        ),
      ),
    )
  }

  /**
   * The list of questions.
   */
  @computed get questionVms() {
    return this._questionVms.items
  }

  /**
   * Whether a breaker question is currently being presented and is
   * the active step.
   */
  @computed get isBreakerActive() {
    return this.questionVms.some(
      (qvm) => qvm.questionVm.editor.type === 'Breaker' && qvm.step.isActive,
    )
  }

  /**
   * The current question step view-model.
   */
  @computed get currentQuestionStepVm() {
    const currentQuestionStepVm = this.questionVms.find((q) => q.step.isActive)
    return currentQuestionStepVm ?? null
  }

  /**
   * Questions as step accessors.
   */
  @computed get stepAccessors(): StepAccessor[] {
    return this.questionVms
  }

  /**
   * Whether we're currently fetching questions.
   */
  @computed get isFetchingQuestions() {
    return this._fetchQuestions.pending
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this._fetchQuestions.reset()
  }

  /** @inheritDoc */
  @action.bound
  flush() {
    this.currentQuestionStepVm?.questionVm.editor.flush()
  }

  /** @inheritDoc */
  async prepare(signal?: AbortSignal) {
    signal?.throwIfAborted()
    runInAction(() => {
      // Reset the questionnaire completion flag before fetching.
      this.isQuestionnaireCompleted = false
    })
    await this._fetchQuestions(null, signal)
  }

  /**
   * Fetches the questions.
   *
   * @param answers
   * @param signal
   * @private
   */
  private _fetchQuestions = task(
    async (answers: AnswersMap | null, signal?: AbortSignal) => {
      try {
        const result = await this._questionStore.fetchQuestions({
          jobId: this._job.id,
          answers: answers,
          signal,
        })

        signal?.throwIfAborted()
        runInAction(() => {
          this._questionListVm.reconcile(result.questions)
          this.isQuestionnaireCompleted = result.intakeCompleted
        })
      } catch (err: unknown) {
        if (isAbortError(err)) {
          return
        }

        // Purposely not re-throwing here since we don't want the pipeline to
        // error.
        console.error('Unable to fetch questions for job', this._job.id, err)
        this._fetchErrorSubject.next({
          title: 'Retrieving the questionnaire failed',
          error: err,
        })
      }
    },
  )
}

/**
 * Factory for creating a {@link QuestionnaireViewModel}.
 */
export type QuestionnaireViewModelFactory = (
  isReadOnly: () => boolean,
  createStep: StepFactory,
) => QuestionnaireViewModel

/** @see QuestionnaireViewModelFactory */
export function createQuestionnaireViewModelFactory(
  job: DraftJob,
  questionListVm: QuestionListViewModel,
  questionStore: QuestionStore,
): QuestionnaireViewModelFactory {
  return (isReadOnly, createStep) =>
    new QuestionnaireViewModel(
      job,
      createStep,
      isReadOnly,
      questionListVm,
      questionStore,
    )
}

/**
 * Wrapper view model for a question. Manages
 * the step VM.
 */
export class QuestionStepViewModel implements StepAccessor {
  /**
   * The step view-model for this question.
   */
  readonly step

  /**
   * Current validation errors.
   */
  @observable accessor validationErrors: string[] = []

  constructor(
    public questionVm: QuestionEditorViewModel,
    private readonly _isDisabled: () => boolean,
    createStep: StepFactory,
  ) {
    this.step = createStep({
      stepId: `question_${questionVm.id.toLowerCase()}`,
      isShortCircuited: () => questionVm.editor.type === 'Breaker',
      isCompleted: () =>
        questionVm.editor.isAnswered &&
        this.validationResult.type === 'Success',
      outlineItemDisplay: () => ({ title: questionVm.title }),
      validate: () => this.validate(),
    })
  }

  /**
   * Question validation result.
   *
   * NOTE: The `validate` method on the question VM does not mutate
   * state, it only determines the current validity of the current state,
   * which makes it safe for use as a computed.
   */
  @computed get validationResult() {
    return this.questionVm.validate()
  }

  /**
   * Whether to disable editor controls.
   */
  @computed get isDisabled() {
    return this._isDisabled()
  }

  /**
   * Validates the question state.
   *
   * Called imperatively rather than as a computed.
   */
  @action.bound
  validate(): boolean {
    if (this.validationResult.type === 'Failed') {
      this.validationErrors = [...this.validationResult.messages]
      return false
    }

    if (this.validationErrors.length) {
      this.validationErrors = []
    }

    return true
  }
}
